import { useEffect, useState } from 'react';
import { useDropzone, FileRejection } from 'react-dropzone';
import { randomString } from '@perch-labs/warbler';
import { useFlags, useLDClient } from 'launchdarkly-react-client-sdk';
import { Select } from '@perch-labs/shrike';
import { Dropzone } from './document-uploader.style';
import { Container } from '../layout';
import camera from '/images/icons/camera.svg';
import { DocumentReviewDialog } from './document-review-dialog';
import { DocumentPreview } from './document-preview/document-preview.component';

export interface DocumentUploaderProps {
  acceptFileTypes: { [key: string]: string[] };
  copy: { documentType: string };
  uploadedDocuments: UploadedDocument[];
  maxDocuments: number;
  pageType: 'numbered' | 'front-back' | 'front' | 'back' | 'multiple';
  pending: boolean;
  onUploadSuccess: (
    file: File,
    base64Image: string,
    pageType: string,
    proofType: string
  ) => void;
  onUploadFailure: (file: FileRejection) => void;
  onDelete: (id: string) => void;
  hide?: boolean;
  funnel: string;
  utilityId: number;
  showOnlyOthersOption?: boolean;
}

type DocumentFile = {
  file: File;
  base64Image: string;
};

export const toBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result!.toString());
    reader.onerror = error => reject(error);
  });
};

export const getFileType = (uploadedDocument: UploadedDocument): string => {
  return uploadedDocument.base64Image?.match(/^data:([^;]+)/)?.[1] || 'pdf';
};

export interface UploadedDocument extends File {
  id: string;
  documentType?: string;
  error?: string;
  uploadId?: string;
  base64Image?: string;
  docName?: string;
  proofType?: string;
}

// NB: instances of File cannot be spread like traditional objects - need to use Object.assign
export const addFileProps = (
  item: File | FileRejection,
  base64Image?: string
): UploadedDocument => {
  if ('errors' in item) {
    const rejection = item;
    return Object.assign(rejection.file, {
      id: randomString(10),
      error: rejection.errors[0].code,
      docName: rejection.file.name,
    });
  } else {
    const file = item;
    return Object.assign(file, {
      id: randomString(10),
      base64Image,
      docName: file.name,
    });
  }
};

export const DocumentUploader = ({
  acceptFileTypes,
  copy,
  uploadedDocuments,
  maxDocuments,
  pageType,
  onDelete,
  onUploadFailure,
  onUploadSuccess,
  pending,
  funnel,
  utilityId,
  hide,
  showOnlyOthersOption = false,
}: DocumentUploaderProps) => {
  const ldClient = useLDClient();
  const custom = {
    funnel,
    utilityId: utilityId!,
  };

  const flag = useFlags();
  const [reviewDocumentFile, setReviewDocumentFile] =
    useState<DocumentFile | null>(null);
  const [enableManualCaptureOfProofs, setEnableManualCaptureOfProofs] =
    useState(false);

  useEffect(() => {
    ldClient?.identify({
      key: 'user_key',
      custom,
    });
  }, [utilityId, funnel]);

  useEffect(() => {
    setEnableManualCaptureOfProofs(flag.enableManualCaptureOfProofs);
  }, [flag]);

  const [proofType, setProofType] = useState('');
  const totalSavedFiles = uploadedDocuments.filter(f => f.id).length;
  const acceptMoreFiles = totalSavedFiles < maxDocuments;

  const documentTypeOptions = showOnlyOthersOption
    ? [{ text: 'Other', value: 'other' }]
    : [
        { text: 'Card', value: 'card' },
        { text: 'Letter', value: 'letter' },
        { text: 'Utility bill', value: 'utility_bill' },
        {
          text: 'Portal screenshot',
          value: 'account_statement',
        },
      ];

  const onDrop = async (accepted: File[], rejected: FileRejection[]) => {
    if (accepted.length) {
      const file = accepted[0];
      const base64Image = await toBase64(file);
      setReviewDocumentFile({ file, base64Image });
    } else if (rejected.length) {
      const rejectedFile = rejected[0];
      onUploadFailure(rejectedFile);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: acceptFileTypes,
    maxSize: 5 * 1000 * 1000, // 5 MB
    maxFiles: maxDocuments,
    multiple: false,
    onDrop,
    validator: (file: File) => {
      const alreadyUploaded = uploadedDocuments
        .map(f => f.docName)
        .includes(file.name);
      return alreadyUploaded
        ? { code: 'already-uploaded', message: 'Already uploaded' }
        : null;
    },
  });

  const uploadNextPageMessage = () => {
    let nextPageDescription: string;
    if (pageType === 'front') {
      nextPageDescription = 'Front';
    } else if (pageType === 'back') {
      nextPageDescription = 'Back';
    } else if (pageType === 'front-back') {
      nextPageDescription = totalSavedFiles ? 'Back' : 'Front';
    } else if (enableManualCaptureOfProofs) {
      return `Upload ${copy.documentType}`;
    } else {
      nextPageDescription = `Page ${totalSavedFiles + 1}`;
    }
    return `Upload ${copy.documentType} (${nextPageDescription})`;
  };

  return (
    <>
      <Container display="flex" flexDirection="column">
        {uploadedDocuments.map(uploadedDocument => (
          <div key={uploadedDocument.id}>
            {enableManualCaptureOfProofs && (
              <Select
                label="Document type"
                name="documentType"
                options={documentTypeOptions}
                minHeight="0px"
                margin={{ top: '8px' }}
                disabled
                value={uploadedDocument.proofType}
              />
            )}
            <DocumentPreview
              errorCode={uploadedDocument.error}
              onDelete={() => onDelete(uploadedDocument.id)}
              base64Image={uploadedDocument.base64Image}
              uploaded={!!uploadedDocument.uploadId}
              fileType={getFileType(uploadedDocument)}
            />
          </div>
        ))}
      </Container>{' '}
      {!hide && enableManualCaptureOfProofs && (
        <Select
          label="Document type"
          name="documentType"
          options={documentTypeOptions}
          onChange={e => setProofType(e)}
          minHeight="0px"
          margin={{ top: '8px' }}
          withEmptyPlaceholder
          required
          value={proofType}
        />
      )}
      {(acceptMoreFiles || proofType) && !pending && !hide && (
        <Dropzone {...getRootProps()}>
          <input data-testid="dropzone" {...getInputProps()} />
          <Container pt={1}>
            <img src={camera} />
          </Container>
          <b>{isDragActive ? 'Drop to upload' : uploadNextPageMessage()}</b>
        </Dropzone>
      )}
      {reviewDocumentFile && (
        <DocumentReviewDialog
          base64Image={reviewDocumentFile.base64Image}
          fileType={reviewDocumentFile.file.type}
          onApprove={base64Image => {
            onUploadSuccess(
              reviewDocumentFile.file,
              base64Image,
              pageType,
              proofType || 'unknown'
            );
            setReviewDocumentFile(null);
            setProofType('');
          }}
          onCancel={() => setReviewDocumentFile(null)}
          onClose={() => setReviewDocumentFile(null)}
        />
      )}
    </>
  );
};
